import _ from "lodash";
import axios from "axios";
import growl from "./growl";
import { PRODUCT_TYPES, base_url } from "./constants";
import EventBus from './event-bus';
import { EVENTS } from './constants';

async function post2Responses(url, payload, firstResponseCallback) {
	try {
		const response = await fetch(url, {
			method: "POST",
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload),
		});

		if (!response.body) {
			growl.error("ReadableStream not yet supported in this browser.");
			throw new Error("ReadableStream not yet supported in this browser.");
		}

		const reader = response.body.getReader();
		let decoder = new TextDecoder();
		let dataBuffer = "";

		let processed = false;
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			dataBuffer += decoder.decode(value, { stream: true });
			// Check if we received the first response
			if (dataBuffer.includes("\n") && !processed) {
				processed = true;
				const parts = dataBuffer.split("\n");
				const firstResponse = parts[0];
				dataBuffer = parts.slice(1).join("\n");

				try {
					const jsonResponse = JSON.parse(firstResponse);
					firstResponseCallback(jsonResponse);
					// console.log("First response:", jsonResponse);
					// Process the first response here
				} catch (error) {
					console.log('first response:', firstResponse);
					console.error("Error parsing first response:", error);
				}
			}
		}
		if (dataBuffer.trim()) {
			try {
				const data = JSON.parse(dataBuffer);
				if (data?.status == "OK") {
					return data;
				} else {
					// need err.response.data.login
					response.data = data;
					return handle401({ response }) || Promise.reject(data);
				}
			} catch (error) {
				return Promise.reject(error);
			}
		}
	} catch (error) {
		console.error("Error making request:", error);
		growl.error("Error making request:");
		throw new Error("Error making request:");
	}
}

function normalizeShopData(shop: Shop) {
	if (!shop) return;
	shop.canOrder = shop.achatActif && !shop.closed;
	shop.Tel = shop.Tel && shop.Tel[0] == "#" && shop.Tel.substr(1);
	shop.Mobile = shop.Mobile && shop.Mobile[0] == "#" && shop.Mobile.substr(1);
	shop.Zip = shop.Zip && shop.Zip[0] == "#" && shop.Zip.substr(1);
	shop.delivery_zones = normalizeDeliveryZonesData(shop.delivery_zones);

	// workaround for when shop.XXX == [] => shop.XXX.prop will NOT be saved !
	// and creating and saving {} returns [] when loaded;
	// all props set to it will be silently ignored when saving
	shop.Theme = _.isEmpty(shop.Theme)?{}:shop.Theme;
	shop.info = _.isEmpty(shop.info)?{}:shop.info;
	shop.info.labels = _.isEmpty(shop.info.labels)?{}:shop.info.labels;
	shop.info.trackers = _.isEmpty(shop.info.trackers)?{}:shop.info.trackers;
	shop.info.confirm = _.isEmpty(shop.info.confirm)?{}:shop.info.confirm;
	shop.info.salon = _.isEmpty(shop.info.salon)?{}:shop.info.salon;
	shop.info.caisse = _.isEmpty(shop.info.caisse)?{}:shop.info.caisse;
	shop.info.upsell = _.isEmpty(shop.info.upsell)?{}:shop.info.upsell;
	shop.info.upsell.formules = _.isEmpty(shop.info.upsell.formules)?{}:shop.info.upsell.formules;
	shop.info.upsell.suggestions = _.isEmpty(shop.info.upsell.suggestions)?{}:shop.info.upsell.suggestions;
	_.defaultsDeep(shop.info,{
		reservationSaleAreaId: 2, // sur place
		labels: {
			Seat: "Position",
			SeatPlaceholder: "Ex: Place 224",
			Adresse: "Adresse",
			Restaurant: "Restaurant",
			AddressCommentaire: "Commentaire d'adresse",
			EnCaisse: "En caisse",
			PassezCommande: "Passez commande",
			Promotion: "Promotion",
			PromotionInitials: "%",
		},
		validateDeliveryAddress: true,
		orderComment: false,
		orderAsap: true,
		minTelDigits: 10,
		displayOneCategory: false,
		closedDescription: "",
		allowOrderStateChange: true,
		landingModal: false,
		display: {
			stretchProductImage: true,
			vignetteDesc: true,
			itemChildList: false,
			hideRootCategory: false,
			hideMenuCompositionInVitrine: false,
		},
		bo_displayOneCategory: true,
		trackers: {},
		reservation: {
			manualValidation: false,
		},
		confirm: {
			clickCollect: true,
		},
		caisse: {
			active: false,
			tvaList: ['20%','10%','5,5%','0%'],
			printerList: ['production'],
		},
		salon: {},
		upsell: {
			suggestions: {
				displayInCart: false,
				displayInCheckout: false,
			},
			formules: {},
		},
	});
	shop.sister_shops = orderSisterShops(shop.sister_shops);
	for (let i in shop.sister_shops) {
		let sister = shop.sister_shops[i];
		normalizeShopData(sister);
		shop.sister_shops[i] = sister;
		sister.sister_shops = shop.sister_shops;
	}
	let name = shop.DefaultDescription;
	shop.DefaultDescription = name && name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
	return shop;
}

function orderSisterShops(sisterShops: Shop[]) {
	let masterShop = _.find(sisterShops, (sister) => sister.ShopId == sister.GroupId);
	let sorted = [];
	if (masterShop?.info.sisterShopsOrder) {
		for (let i in masterShop.info.sisterShopsOrder) {
			let ShopId = masterShop.info.sisterShopsOrder[i];
			let shopIndex = _.findIndex(sisterShops, (sister) => sister.ShopId == ShopId);
			if (shopIndex != -1) {
				let [sister] = sisterShops.splice(shopIndex, 1);
				sorted.push(sister);
			}
		}
	}
	// sort remaining by DefaultName, and append to sorted
	let rest = _.sortBy(sisterShops, (sister) => sister.DefaultName?.toLowerCase());
	return sorted.concat(rest);
}

function normalizeDeliveryZonesData(delivery_zones) {
	for (var i in delivery_zones) {
		if (delivery_zones[i].Zip && delivery_zones[i].Zip[0] == "#") {
			delivery_zones[i].Zip = delivery_zones[i].Zip.substr(1);
		}
	}
	return delivery_zones;
}

function getSaleModes() {
	return get(base_url + "/admin/shop/sale-modes", "Erreur pour les SaleModes").then((data) => _.keyBy(data.saleModes, "ModeId"));
}

function getSaleAreas() {
	return get(base_url + "/admin/shop/sale-areas", "Erreur pour les SalesAreas").then((data) => {
		let saleAreas = _.map(data.saleAreas, function (saleArea) {
			saleArea.SaleAreaId = "" + saleArea.SaleAreaId;
			return saleArea;
		});
		return _.keyBy(saleAreas, "SaleAreaId");
	});
}

function normalizeClientData(client: Client) {
	if (!client) return client;

	client.addresses = _.filter(client.addresses, (addr) => addr["Address"] || addr["Zip"] || addr["City"] || addr["PlusCode"]);
	client.cards = client.cards || [];
	if ("" + client.Mobile?.[0] == "#") {
		client.Mobile = client.Mobile.substr(1);
	}
	return client;
}

function normalizeOrderData(order) {
	order.Total = _.round(this.order.Total, 2);
	normalizeClientData(order.client);
	normalizeShopData(order.shop);
}

function deepLinkTree(item, parent, products, tid = { value: 0 }) {
	item.tid = tid.value++;
	item.parent = parent;
	item.product = products[item.id];
	for (var i in item.childs) {
		deepLinkTree(item.childs[i], item, products, tid);
	}
	return item;
}

let getProductTree = memoize(_getProductTree);
function _getProductTree(ShopId) {
	var qProducts = getProducts(ShopId);
	var qProductTree = axios.get(base_url + "/admin/tree?ShopId=" + ShopId).then((res) => res.data);
	return Promise.all([qProducts, qProductTree]).then(([products, productTree]) => {
		var treeRoot = { id: 0, childs: productTree, ShopId: ShopId };
		deepLinkTree(treeRoot, null, products);
		return treeRoot;
	});
}

let getFinalProducts = memoize(_getFinalProducts);
function _getFinalProducts(ShopId) {
	return axios.get(base_url + "/admin/products/final-products?ShopId=" + ShopId).then(function (res) {
		return _.keyBy(res.data, "FinalProductId");
	});
}

let getProducts = memoize(_getProducts);
function _getProducts(ShopId) {
	var qProducts = axios.get(base_url + "/admin/products?ShopId=" + ShopId).then(function (res) {
		return _.keyBy(res.data, "id");
	});
	var qFinalProducts = getFinalProducts(ShopId);

	// for each product, fix ims, round dp and attach its finalProducts
	return Promise.all([qFinalProducts, qProducts]).then(function ([finalProducts, products]) {
		let rootProductId = null;
		_.forEach(products, function (p) {
			if (!p.imt) {
				p.imt = p.ims;
			}
			_.assign(p, {
				dp: _.round(p.dp, 2),
				finalProducts: _.filter(finalProducts, {
					ProductId: p.id,
				}),
			});
			_.forEach(p.finalProducts, (fp) => (fp.product = p));
			if (p.ty == PRODUCT_TYPES.RootCategory) {
				rootProductId = p.id;
			}
		});
		if (rootProductId) {
			products[0] = products[rootProductId];
		} else {
			products[0] = {
				ShopId: ShopId,
				id: 0,
				ty: 5,
				dn: "Carte & Menus",
				av: 1,
				ims: " ",
				dd: "",
				dp: 0,
			};
		}
		return products;
	});
}

let getOptionGroups = memoize(_getOptionGroups);
function _getOptionGroups(ShopId) {
	return axios.get(base_url + "/admin/option-groups?ShopId=" + ShopId).then(function (res) {
		return _.keyBy(res.data, "id");
	});
}

let getTagList = memoize(_getTagList);
function _getTagList(ShopId: number = null) {
	let url = base_url + "/admin/products/tag-list?ShopId=" + ShopId;
	return get(url, "Erreur pour obtenir les tags").then((data) => data.tags);
}

// memoize function, but if the Promise fails, delete result from cache,
// so it wont fail again on retry
function memoize(fn: (...args: any) => any) {
	let memoized = _.memoize(fn);
	function fn2(...args: any) {
		return memoized(...args).catch((err) => {
			memoized.cache.delete(args[0]);
			return Promise.reject(err);
		});
	}
	return fn2;
}

let getMenus = memoize(_getMenus);
function _getMenus(ShopId) {
	var qProducts = getProducts(ShopId);
	var qProductTree = getProductTree(ShopId);
	var qFinalProducts = getFinalProducts(ShopId);
	var qCategories = axios.get(base_url + "/admin/menus/categories?ShopId=" + ShopId).then(function (res) {
		return _.groupBy(res.data, "MenuId");
	});
	// group joinPirces by MenuId, then index by FinalProductId
	var qJoinPrices = axios
		.get(base_url + "/admin/menus/join-prices?ShopId=" + ShopId)
		.then(function (res) {
			return _.groupBy(res.data, "MenuId");
		})
		.then(function (res) {
			return _.mapValues(res, function (join_prices) {
				return _.keyBy(join_prices, "FinalProductId");
			});
		});

	// for each menu.menuCategory, attach its corresponding treeItem
	return Promise.all([qProducts, qCategories, qFinalProducts, qProductTree, qJoinPrices]).then(function ([
		products,
		categoriesByMenus,
		finalProducts,
		productTree,
		joinPrices,
	]) {
		var menus = _(products)
			.filter((product) => product.ty == 2)
			.map((onlyAMenu) => _.defaults(onlyAMenu, { joinPrices: {} }))
			.keyBy("id")
			.value();
		_.forEach(categoriesByMenus, function (categories, menuId) {
			products[menuId].categories = categories || [];
			menus[menuId] = products[menuId];
			menus[menuId].joinPrices = joinPrices[menuId] || {};
			_.forEach(categories, function (category) {
				category.treeItem = treeItemForProductId(category.CategoryId, productTree);
				category.menuCompos = [];
				for (var i in category.menu_compo_ids) {
					category.menuCompos.push(finalProducts[category.menu_compo_ids[i]]);
				}
				// menu_compo_ids is now unneeded
				delete category.menu_compo_ids;

				category.joinPrices = joinPrices[menuId] || {};
			});
		});
		// interet ?
		return menus;
	});
}

function treeItemForProductId(pid, productTree) {
	if (productTree.id == pid) return productTree;
	for (var i in productTree.childs) {
		var treeItem = treeItemForProductId(pid, productTree.childs[i]);
		if (treeItem) return treeItem;
	}
	return null;
}

let memoizedGetShopInfo = memoize(_getShopInfo);
let getShopInfo = (ShopId, maybeCarteId = null) => memoizedGetShopInfo(ShopId, maybeCarteId).finally(() => notifyAngularJs());
function _getShopInfo(ShopId: number, maybeCarteId: number = null) {
	if (!ShopId) {
		return Promise.resolve(null);
	}
	let qShop = get(base_url + "/admin/user/shop-info?ShopId=" + ShopId, "Erreur de chargement des données magasin").then((data) =>
		normalizeShopData(data.shop)
	);
	// let qCarteId = maybeCarteId ? Promise.resolve(maybeCarteId) : qShop.then((shop) => shop.CarteId);
	// return qCarteId.then((CarteId: number) => {
	return (
		Promise.all([qShop, getProductTree(ShopId), getOptionGroups(ShopId), getProducts(ShopId), getFinalProducts(ShopId), getMenus(ShopId)])
			// })
			.then(([shop, productTree, optionGroups, products, finalProducts, menus]) => {
				if (shop) {
					shop.productTree = productTree;
					shop.optionGroups = optionGroups;
					shop.products = products;
					shop.finalProducts = finalProducts;
					shop.menus = menus;

					ensureRootProductHasImage(products, shop);
				}
				return shop;
			})
			.catch((err) => {
				console.error(err);
				return Promise.reject(err);
			})
	);
}

function ensureRootProductHasImage(products, shop) {
	let rootProduct = products[0];
	if (!rootProduct.ims) {
		rootProduct.ims = shop.Image || "";
		rootProduct.imt = shop.Image;
	}
}

function uploadImage(file, id, url) {
	if (!file || _.isString(file)) {
		return Promise.resolve(file);
	} else {
		let formData = new FormData();
		formData.append("id", id);
		formData.append("filename", file.name.split("?")[0]);
		formData.append("file", file);
		let config = { headers: { "Content-Type": "multipart/form-data" } };
		return axios
			.post(url, formData, config)
			.then((res) => (res.data.status == "OK" ? res.data.filename : Promise.reject("Echec de l'upload d'image: " + res.data.msg)))
			.catch((err) => err && growl.error(err));
		// append unique querystring to force cache update
		// see https://stackoverflow.com/questions/1431512/is-there-a-way-to-force-browsers-to-refresh-download-images
		// .then((filename) => this.shop.Image = filename + '?' + _.uniqueId())
	}
}

function post(url: string, payload: {}, errorMsg: string) {
	let promise = axios.post(url, payload);
	return postThen(promise, errorMsg);
}

function get(url: string, errorMsg: string) {
	let promise = axios.get(url);
	return postThen(promise, errorMsg);
}

async function postThen(promise: Promise<any>, errorMsg: string) {
	notifyAngularJs();
	return promise.then(function(res) {
		if (res.data && res.data.status == 'OK') {
			return res.data
		} else {
			return Promise.reject(res)
		}
	})
	.catch(function(err) {
		// ensure the rejection will be handled by window.onunhandledrejection
		Promise.reject(err)
		let response;
		if (response = handle401(err)) {
			return response;
		}
		if (errorMsg === '') {
			// '' means no warning
			return Promise.reject(err);
		}
		// else build up the error msg
		let arr = [errorMsg];
		if (err.data?.msg) {
			arr.push(err.data.msg);
		}
		if (err.response?.data?.msg) {
			arr.push( err.response.data.msg);
		}
		errorMsg = _.compact(arr).join(': ');
		if (err.response?.status != 500) {
			growl.warning(errorMsg || 'Erreur lors de la demande');
		} else {
			growl.error(errorMsg || 'Erreur lors de la demande')
		}
		return Promise.reject(err)
	})
	.finally(() => notifyAngularJs());
}

function handle401(err) {
	if (err.response?.status == 401) {
		if (err.response.data?.login) {
			// errorMsg = ''; // disable error message, replace with the following one:
			growl.info('Connectez-vous pour accéder à cette page');
			switch(err.response.data?.login) {
				case 'client':
					EventBus.$emit(EVENTS.requestClientLogin)
					break;
				case 'admin':
					EventBus.$emit(EVENTS.requestAdminLogin)
					break;
				case 'manager':
					EventBus.$emit(EVENTS.requestManagerLogin)
					break;
			}
			return Promise.reject(err);
		}
	}
	return false;
}

function notifyAngularJs() {
	if (typeof angular == "undefined") {
		return;
	}
	var $body = angular.element(document.body); // 1
	if ($body.scope()) {
		var $rootScope = $body.scope().$root; // 2
		$rootScope.$evalAsync();
	}
}

// shorthand method for building full url with ClientId query param
function urlize(method, queryParams) {
	return _.compact([
		base_url + "/admin/" + method,
		_.map(queryParams, function (v, k) {
			return k + "=" + v;
		}).join("&"),
	]).join("?");
}

export default {
	post2Responses,
	normalizeShopData,
	normalizeClientData,
	normalizeDeliveryZonesData,
	normalizeOrderData,
	getProductTree,
	getProducts,
	getOptionGroups,
	getMenus,
	getFinalProducts,
	getShopInfo,
	getSaleAreas,
	getSaleModes,
	post,
	get,
	postThen,
	urlize,
	notifyAngularJs,
	uploadImage,
	getTagList,
};
